// Responsive styling hook for expanded panel state
// Returns different font sizes, spacing, and chart dimensions based on expanded state

export interface ExpandedStylesResult {
  fonts: {
    pageTitle: string;
    sectionTitle: string;
    kpiValue: string;
    kpiLabel: string;
    kpiChange: string;
    tableHeader: string;
    tableCell: string;
    tableCellHighlight: string;
    chartLabel: string;
    footnote: string;
    source: string;
  };
  spacing: {
    container: string;
    section: string;
    gap: string;
    cardPadding: string;
  };
  sizes: {
    chartHeight: string;
    chartHeightLarge: string;
    chartHeightSmall: string;
    kpiCard: string;
    tableRowHeight: string;
    iconSize: number;
    iconSizeLarge: number;
  };
  chart: {
    axisFontSize: number;
    tooltipFontSize: number;
    legendFontSize: number;
    strokeWidth: number;
    dotRadius: number;
    barRadius: number;
    tooltipPadding: number;
  };
  show: {
    extraData: boolean;
    sparklines: boolean;
    detailedTooltips: boolean;
    additionalColumns: boolean;
    fullDescriptions: boolean;
  };
  itemsToShow: {
    tableRows: number;
    chartDataPoints: number;
    legendItems: number;
  };
  isExpanded: boolean;
}

export const useExpandedStyles = (isExpanded: boolean = false): ExpandedStylesResult => {
  return {
    fonts: {
      // Page/panel title
      pageTitle: isExpanded
        ? 'text-2xl font-bold tracking-wide'
        : 'text-xs font-semibold uppercase',
      // Section headers within panel
      sectionTitle: isExpanded
        ? 'text-lg font-semibold uppercase tracking-wider'
        : 'text-[10px] font-medium uppercase',
      // KPI/metric values (the big numbers)
      kpiValue: isExpanded
        ? 'text-5xl font-bold'
        : 'text-2xl font-bold',
      kpiLabel: isExpanded
        ? 'text-sm uppercase tracking-wide'
        : 'text-[9px] uppercase',
      kpiChange: isExpanded
        ? 'text-lg font-medium'
        : 'text-xs',
      // Table typography
      tableHeader: isExpanded
        ? 'text-sm font-semibold uppercase tracking-wide'
        : 'text-[9px] font-medium uppercase',
      tableCell: isExpanded
        ? 'text-base'
        : 'text-[10px]',
      tableCellHighlight: isExpanded
        ? 'text-base font-semibold'
        : 'text-[10px] font-semibold',
      // Chart labels
      chartLabel: isExpanded
        ? 'text-sm'
        : 'text-[9px]',
      // Supporting text
      footnote: isExpanded
        ? 'text-sm'
        : 'text-[9px]',
      source: isExpanded
        ? 'text-sm italic'
        : 'text-[9px] italic',
    },
    spacing: {
      container: isExpanded
        ? 'p-6 lg:p-8'
        : 'p-3',
      section: isExpanded
        ? 'mb-8'
        : 'mb-3',
      gap: isExpanded
        ? 'gap-6'
        : 'gap-2',
      cardPadding: isExpanded
        ? 'p-6'
        : 'p-3',
    },
    sizes: {
      chartHeight: isExpanded
        ? 'h-[50vh] min-h-[400px]'
        : 'h-[150px]',
      chartHeightLarge: isExpanded
        ? 'h-[60vh] min-h-[500px]'
        : 'h-[200px]',
      chartHeightSmall: isExpanded
        ? 'h-[30vh] min-h-[250px]'
        : 'h-[100px]',
      kpiCard: isExpanded
        ? 'p-6 min-h-[140px]'
        : 'p-3',
      tableRowHeight: isExpanded
        ? 'h-12'
        : 'h-8',
      iconSize: isExpanded ? 20 : 14,
      iconSizeLarge: isExpanded ? 28 : 18,
    },
    chart: {
      axisFontSize: isExpanded ? 14 : 10,
      tooltipFontSize: isExpanded ? 14 : 11,
      legendFontSize: isExpanded ? 14 : 10,
      strokeWidth: isExpanded ? 3 : 2,
      dotRadius: isExpanded ? 6 : 4,
      barRadius: isExpanded ? 6 : 3,
      tooltipPadding: isExpanded ? 16 : 8,
    },
    show: {
      extraData: isExpanded,
      sparklines: isExpanded,
      detailedTooltips: isExpanded,
      additionalColumns: isExpanded,
      fullDescriptions: isExpanded,
    },
    itemsToShow: {
      tableRows: isExpanded ? 15 : 8,
      chartDataPoints: isExpanded ? 20 : 10,
      legendItems: isExpanded ? 8 : 5,
    },
    isExpanded,
  };
};

// Utility function to get chart tooltip styles
export const getTooltipStyle = (isExpanded: boolean) => ({
  backgroundColor: '#0a0a0f',
  border: '1px solid #00d26a',
  borderRadius: isExpanded ? '8px' : '6px',
  fontSize: isExpanded ? '14px' : '11px',
  padding: isExpanded ? '14px 18px' : '8px 12px',
  boxShadow: '0 4px 20px rgba(0, 210, 106, 0.15)',
});

// Utility function to get chart axis tick props
export const getAxisTickProps = (isExpanded: boolean) => ({
  fontSize: isExpanded ? 14 : 10,
  fill: '#6b7280',
});

// Utility function to get chart axis line props
export const getAxisLineProps = () => ({
  stroke: '#1e1e26',
});

export default useExpandedStyles;
